// client/src/utils/storageManager.js
// Centralized storage management with quota handling for iOS compatibility

import toastr from 'toastr'

/**
 * Priority levels for storage entries
 * Higher priority items are preserved during quota cleanup
 */
const STORAGE_PRIORITY = {
  CRITICAL: 3, // Auth tokens, user session data
  HIGH: 2, // Registration state, form data
  MEDIUM: 1, // UI preferences, cached data
  LOW: 0, // Analytics events, temporary caches
}

/**
 * Storage manager with iOS-compatible quota handling
 * Automatically clears low-priority data when quota is exceeded
 */
export class StorageManager {
  /**
   * Set an item in localStorage with quota handling
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @param {number} priority - Priority level (use STORAGE_PRIORITY constants)
   * @returns {boolean} - True if successful, false if failed
   */
  static set(key, value, priority = STORAGE_PRIORITY.MEDIUM) {
    try {
      localStorage.setItem(key, value)

      // Store priority metadata for cleanup decisions
      try {
        const priorities = JSON.parse(
          localStorage.getItem('_storage_priorities') || '{}',
        )
        priorities[key] = priority
        localStorage.setItem('_storage_priorities', JSON.stringify(priorities))
      } catch {
        // Ignore priority metadata errors
      }

      return true
    } catch (e) {
      if (e.name === 'QuotaExceededError' || e.code === 22) {
        console.warn('Storage quota exceeded, attempting cleanup')

        // Try to clear low-priority data
        const cleared = this.clearLowPriorityData()

        if (cleared) {
          // Retry storage
          try {
            localStorage.setItem(key, value)
            console.log('Storage successful after cleanup')
            return true
          } catch (retryError) {
            console.error('Storage failed even after cleanup:', retryError)
          }
        }

        // If still failing, notify user
        this.showStorageError()
        return false
      }

      // Other storage errors (e.g., private browsing mode)
      console.error('Storage error:', e)
      return false
    }
  }

  /**
   * Get an item from localStorage
   * @param {string} key - Storage key
   * @returns {string|null} - Stored value or null
   */
  static get(key) {
    try {
      return localStorage.getItem(key)
    } catch (e) {
      console.error('Error reading from storage:', e)
      return null
    }
  }

  /**
   * Remove an item from localStorage
   * @param {string} key - Storage key
   */
  static remove(key) {
    try {
      localStorage.removeItem(key)

      // Clean up priority metadata
      try {
        const priorities = JSON.parse(
          localStorage.getItem('_storage_priorities') || '{}',
        )
        delete priorities[key]
        localStorage.setItem('_storage_priorities', JSON.stringify(priorities))
      } catch {
        // Ignore metadata errors
      }
    } catch (e) {
      console.error('Error removing from storage:', e)
    }
  }

  /**
   * Clear low-priority data to free up space
   * @returns {boolean} - True if any data was cleared
   */
  static clearLowPriorityData() {
    try {
      const priorities = JSON.parse(
        localStorage.getItem('_storage_priorities') || '{}',
      )
      let clearedAny = false

      // Clear items by priority (lowest first)
      const priorityLevels = [STORAGE_PRIORITY.LOW, STORAGE_PRIORITY.MEDIUM]

      for (const level of priorityLevels) {
        const keys = Object.keys(priorities).filter(
          (k) => priorities[k] === level,
        )

        for (const key of keys) {
          try {
            localStorage.removeItem(key)
            delete priorities[key]
            clearedAny = true
            console.log(`Cleared low-priority item: ${key}`)
          } catch {
            // Continue trying other keys
          }
        }

        // If we cleared some data, update priorities and return
        if (clearedAny) {
          try {
            localStorage.setItem(
              '_storage_priorities',
              JSON.stringify(priorities),
            )
          } catch {
            // Ignore metadata errors
          }
          return true
        }
      }

      // If no prioritized items found, clear known cache prefixes
      return this.clearKnownCaches()
    } catch (e) {
      console.error('Error during low-priority cleanup:', e)
      return this.clearKnownCaches()
    }
  }

  /**
   * Clear known cache prefixes as fallback cleanup strategy
   * @returns {boolean} - True if any data was cleared
   */
  static clearKnownCaches() {
    const cachePrefixes = ['flex_chat_messages_', 'analytics_', 'servesync_']

    let clearedAny = false

    try {
      const keys = Object.keys(localStorage)
      for (const key of keys) {
        for (const prefix of cachePrefixes) {
          if (key.startsWith(prefix)) {
            try {
              localStorage.removeItem(key)
              clearedAny = true
              console.log(`Cleared cache item: ${key}`)
            } catch {
              // Continue trying other keys
            }
          }
        }
      }
    } catch (e) {
      console.error('Error clearing caches:', e)
    }

    return clearedAny
  }

  /**
   * Show user-friendly error message about storage issues
   */
  static showStorageError() {
    toastr.error(
      'Storage is full. Some features may not work correctly. Try clearing browser data or closing other tabs.',
      'Storage Error',
      { timeOut: 8000 },
    )
  }

  /**
   * Get estimated storage usage (if available)
   * @returns {Promise<{usage: number, quota: number}|null>}
   */
  static async getStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      try {
        const estimate = await navigator.storage.estimate()
        return {
          usage: estimate.usage || 0,
          quota: estimate.quota || 0,
          percentage: estimate.quota
            ? ((estimate.usage / estimate.quota) * 100).toFixed(1)
            : 0,
        }
      } catch (e) {
        console.error('Error getting storage estimate:', e)
      }
    }
    return null
  }
}

// Export priority constants for use in application code
export { STORAGE_PRIORITY }

export default StorageManager
